<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <div v-if="items.length" class="table">
          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber }}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong>&nbsp;<span class="text--lowercase">{{ $t(`${namespace}.invoices`) }}</span>
          </div>

          <v-layout class="table__header" align-center>
            <v-flex class="table__checkbox" xs1 />
            <v-flex xs1># {{ $t('Common.note') }}</v-flex>
            <v-flex xs2>Status</v-flex>
            <v-flex xs3>{{ $t('Common.company') | capitalizeFirstLetter }}</v-flex>
            <v-flex xs1>{{ $t('Common.maturity') }}</v-flex>
            <v-flex xs1>{{ $t(`${namespace}.updatedDate`) }}</v-flex>
            <v-flex xs2>{{ $t('Common.amount') }}</v-flex>
            <v-flex xs2>{{ $t('Components.FinancialInvoicesHistory.billingType') }}</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile
                class="table__body__tr"
                :class="{ 'table__body__tr--active': activateLine(item) }"
                @click="handleClick($event, item)"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex class="table__checkbox table__td--prevent" xs1>

                        <!--
                          AUTHORISATION_SENT    => Pendente de autorização (cartão de crédito)
                          AUTHORISED            => Autorizado
                          CAPTURED_SENT         => Aguardando pagamento
                          CAPTURED              => Pago
                          REFUSED               => Recusado
                          REPROCESSING_PAYMENT  => Reprocessando
                         -->
                        <v-tooltip
                          v-if="hasRole('billing', 'ROLE_PATCH_UPDATE_PAYMENT_STATUS') && item.paymentStatus !== 'CAPTURED'"
                          slot="append"
                          top
                        >
                          <v-btn
                            color="gray-6"
                            class="mx-0"
                            slot="activator"
                            flat
                            icon
                            @click="openApprovePaymentDialog(item)"
                          >
                            <v-icon>credit_score</v-icon>
                          </v-btn>
                          <span>{{ $t(`${namespace}.approvePaymentTooltip`) }}</span>
                        </v-tooltip>
                      </v-flex>

                      <v-flex xs1>
                        <span class="text--ellipsis">
                          {{ item.numericalId }}
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <!--
                          AUTHORISATION_SENT    => Pendente de autorização (cartão de crédito)
                          AUTHORISED            => Autorizado
                          CAPTURED_SENT         => Aguardando pagamento
                          CAPTURED              => Pago
                          REFUSED               => Recusado
                          REPROCESSING_PAYMENT  => Reprocessando
                         -->
                        <StatusBullet :status="item.paymentStatus" />
                      </v-flex>

                      <v-flex xs3>
                        <span class="text--ellipsis">
                          {{ item.company.companyName | ellipsis(40) }}
                          <span class="gray-6--text caption text--ellipsis">ID: {{ item.company.companyId }}</span>
                        </span>
                      </v-flex>

                      <v-flex xs1>
                        <span class="text--ellipsis">
                          {{ moment(item.dueDateOpt).format('DD/MM/YY') }}
                        </span>
                      </v-flex>

                      <v-flex xs1>
                        <span class="text--ellipsis">
                          {{ moment(item.updateDateOpt).format('DD/MM/YY') }}
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span class="text--ellipsis">
                          {{ item.totalValue | currencyBRL }}
                        </span>
                      </v-flex>

                      <v-flex xs2>
                        <span class="text--ellipsis">
                          <span :title="getBilletType(item.billType)">{{ getBilletType(item.billType) }}</span>
                        </span>
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="employee"
          :text="$t('Common.emptyFinancial')"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          style="margin-bottom: 120px"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>

    <GlobalDrawer
      :show="globalDrawer.visibility"
      :drawerType="globalDrawer.type"
      :data="globalDrawer.data"
      :actionType="globalDrawer.action"
      :filters="globalDrawer.filters"
      @close="closeGlobalDrawer"
    />

    <ApprovePaymentDialog
      :visible="approvePaymentDialog"
      :data="typeof selectedItem === 'object' ? selectedItem : {}"
      @close="closeApprovePaymentDialog"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { hasRole, formatNumber } from 'utils/functions'
import EmptyState from 'components/EmptyState'
import GlobalDrawer from 'components/globalDrawer'
import StatusBullet from 'components/company/StatusBullet'
import ApprovePaymentDialog from './ApprovePaymentDialog'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    },
    drawerVisibility: {
      type: Boolean
    }
  },
  components: {
    EmptyState,
    GlobalDrawer,
    StatusBullet,
    ApprovePaymentDialog
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany
    }),

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.PaymentStatusPage.table',
      selectedItem: null,
      globalDrawer: {
        visibility: false,
        type: '',
        data: {},
        filters: {},
        action: null
      },
      approvePaymentDialog: false
    }
  },
  methods: {
    hasRole (group, role) {
      return hasRole(group, role)
    },

    openGlobalDrawer ({ data = {}, filters = {}, action = 'preview' }) {
      this.globalDrawer = {
        visibility: true,
        type: 'approvePayment',
        data,
        filters,
        action
      }
    },

    closeGlobalDrawer (reload = null) {
      this.globalDrawer = {
        visibility: false,
        type: '',
        data: {},
        filters: {},
        action: null
      }

      this.selectedItem = null
      reload && this.$emit('reloadData')
    },

    handleEditItem (item) {
      this.openGlobalDrawer({
        data: {
          id: item.id
        },
        action: 'edit'
      })

      this.selectedItem = item.id
    },

    handlePage (page) {
      this.closeGlobalDrawer()
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    activateLine (item) {
      return item.numericalId === this.selectedItem
    },

    handleClick ($event, item) {
      const cls = $event.target.classList

      if (
        !cls.contains('v-btn__content') &&
        !cls.contains('v-icon') &&
        !cls.contains('v-input--selection-controls__ripple') &&
        !cls.contains('accent--text')
      ) {
        this.selectedItem = item.numericalId
        this.openGlobalDrawer({
          data: { ...item }
        })
      }
    },

    openApprovePaymentDialog (item) {
      this.approvePaymentDialog = true
      this.selectedItem = item
    },

    closeApprovePaymentDialog (reload) {
      this.approvePaymentDialog = false
      this.selectedItem = null
      reload && this.handleReloadData()
    },

    getBilletType (type) {
      const types = {
        'BOLETO': this.$t('Components.CompanyFinancialConfig.bankBillet'),
        'CREDIT_CARD': this.$t('Components.CompanyFinancialConfig.creditCard'),
        'BANK_TRANSFER': this.$t('Components.CompanyFinancialConfig.bankTransfer')
      }

      return types[type]
    }
  },
  watch: {
    selectedCompany () {
      this.closeGlobalDrawer()
    },

    drawerVisibility () {
      this.closeGlobalDrawer()
    }
  }
}
</script>
